import React from "react";
import { useTranslation } from "react-i18next";
import { GwSummary, SummaryEvent } from "../../api/leagues";
import { panelHeading } from "../../styles";

interface Props {
  gwSummary: GwSummary | null;
  members: { id: string; displayName: string }[];
  isStarted: boolean;
}

const panelStyle: React.CSSProperties = {
  background: "var(--color-surface)",
  border: "1px solid var(--color-border-subtle)",
  borderRadius: 12,
  padding: "1rem 1.25rem",
  marginBottom: "1rem",
};

const highlightStyle: React.CSSProperties = {
  color: "var(--color-accent)",
  fontWeight: 600,
};

const COMPACT_LIST_THRESHOLD = 3;
const MAX_NAME_CHARS = 18;

// Long display names blow the character budget fast across a multi-sentence summary —
// truncate with an ellipsis (only when that's actually shorter than the full name).
function truncateName(name: string): string {
  if (name.length <= MAX_NAME_CHARS) return name;
  return `${name.slice(0, MAX_NAME_CHARS - 1).trimEnd()}…`;
}

interface Sentence {
  text: string;
  highlights: string[];
}

function joinHuman(items: string[], conjunction: string = "and"): string {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} ${conjunction} ${items[1]}`;
  return `${items.slice(0, -1).join(", ")} ${conjunction} ${items[items.length - 1]}`;
}

// Collapses long name lists to "N players" / "N clubs" — slots straight into
// {{members}}/{{clubNames}}/{{pairs}} placeholders, since "4 clubs were all
// eliminated..." reads fine as a sentence. The collapsed phrase itself becomes
// the highlight when there are too many names to highlight individually.
function namesOrCount(items: string[], unit: "players" | "clubs"): { text: string; highlights: string[] } {
  if (items.length > COMPACT_LIST_THRESHOLD) {
    const text = `${items.length} ${unit}`;
    return { text, highlights: [text] };
  }
  return { text: joinHuman(items), highlights: items };
}

function ordinal(n: number): string {
  const abs = Math.abs(n);
  const lastTwo = abs % 100;
  const last = abs % 10;
  if (lastTwo >= 11 && lastTwo <= 13) return `${n}th`;
  if (last === 1) return `${n}st`;
  if (last === 2) return `${n}nd`;
  if (last === 3) return `${n}rd`;
  return `${n}th`;
}

function parseGroupsStr(groupsStr: string): { score: number; ids: string[] }[] {
  return groupsStr.split(";").map((g) => {
    const colonIdx = g.indexOf(":");
    const score = Number(g.slice(0, colonIdx));
    const ids = g.slice(colonIdx + 1).split(",").filter(Boolean);
    return { score, ids };
  });
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function renderWithHighlights(text: string, highlights: string[], keyPrefix: string): React.ReactNode[] {
  const terms = Array.from(new Set(highlights.filter(Boolean))).sort((a, b) => b.length - a.length);
  if (terms.length === 0) return [text];
  const pattern = new RegExp(`(${terms.map(escapeRegExp).join("|")})`, "g");
  return text.split(pattern).map((part, i) =>
    terms.includes(part) ? <span key={`${keyPrefix}-${i}`} style={highlightStyle}>{part}</span> : part
  );
}

export default function GwSummaryPanel({ gwSummary, members, isStarted }: Props) {
  const { t } = useTranslation();

  if (!isStarted) return null;

  const nameOf = (memberId: string): string =>
    truncateName(members.find((m) => m.id === memberId)?.displayName ?? t("league.summary.unknownMember"));

  const renderSentence = (ev: SummaryEvent, connector: string): Sentence => {
    const { params } = ev;
    const count = typeof params.count === "number" ? params.count : undefined;

    switch (ev.type) {
      case "LEAD_CHANGE": {
        const newLeader = nameOf(params.newLeaderMemberId as string);
        const previousLeader = nameOf(params.previousLeaderMemberId as string);
        return {
          text: connector + t("league.summary.events.LEAD_CHANGE", { newLeader, previousLeader, score: params.score }),
          highlights: [newLeader, previousLeader],
        };
      }
      case "HIGH_SCORE": {
        if (count && count > 1) {
          const ids = (params.memberIds as string).split(",");
          const { text: members, highlights } = namesOrCount(ids.map(nameOf), "players");
          if (params.gwScore !== undefined) {
            return {
              text: connector + t("league.summary.events.HIGH_SCORE_other_same", { members, gwScore: params.gwScore, leagueAvg: params.leagueAvg, count }),
              highlights,
            };
          }
          return {
            text: connector + t("league.summary.events.HIGH_SCORE_other", { members, leagueAvg: params.leagueAvg, count }),
            highlights,
          };
        }
        const member = nameOf(params.memberId as string);
        return {
          text: connector + t("league.summary.events.HIGH_SCORE", { member, gwScore: params.gwScore, leagueAvg: params.leagueAvg }),
          highlights: [member],
        };
      }
      case "GW_TOP_SCORER": {
        if (count && count > 1) {
          const ids = (params.memberIds as string).split(",");
          const names = ids.map(nameOf);
          return {
            text: connector + t("league.summary.events.GW_TOP_SCORER_other", {
              members: joinHuman(names, "&"), gwScore: params.gwScore, count,
            }),
            highlights: names,
          };
        }
        const member = nameOf(params.memberId as string);
        return {
          text: connector + t("league.summary.events.GW_TOP_SCORER", { member, gwScore: params.gwScore }),
          highlights: [member],
        };
      }
      case "ALL_TIED_GW_SCORE": {
        return {
          text: connector + t("league.summary.events.ALL_TIED_GW_SCORE", { gwScore: params.gwScore }),
          highlights: [],
        };
      }
      case "BIG_RISER":
      case "BIG_FALLER": {
        if (count && count > 1) {
          const ids = (params.memberIds as string).split(",");
          const { text: members, highlights } = namesOrCount(ids.map(nameOf), "players");
          return {
            text: connector + t(`league.summary.events.${ev.type}`, { members, count }),
            highlights,
          };
        }
        const member = nameOf(params.memberId as string);
        return {
          text: connector + t(`league.summary.events.${ev.type}`, {
            member, delta: params.delta, toRank: ordinal(params.toRank as number), fromRank: ordinal(params.fromRank as number),
          }),
          highlights: [member],
        };
      }
      case "TARGET_HIT":
      case "TARGET_BUST": {
        if (count && count > 1) {
          const ids = (params.memberIds as string).split(",");
          const { text: members, highlights } = namesOrCount(ids.map(nameOf), "players");
          return {
            text: connector + t(`league.summary.events.${ev.type}`, { members, target: params.target, count }),
            highlights,
          };
        }
        const member = nameOf(params.memberId as string);
        return {
          text: connector + t(`league.summary.events.${ev.type}`, {
            member, totalGoals: params.totalGoals, target: params.target,
          }),
          highlights: [member],
        };
      }
      case "CLOSE_GAP": {
        const memberA = nameOf(params.memberIdA as string);
        const memberB = nameOf(params.memberIdB as string);
        return {
          text: connector + t("league.summary.events.CLOSE_GAP", {
            memberA, memberB, gap: params.gap, count: params.gap as number,
          }),
          highlights: [memberA, memberB],
        };
      }
      case "BIG_LEAD": {
        const member = nameOf(params.memberId as string);
        const nextMember = nameOf(params.nextMemberId as string);
        return {
          text: connector + t("league.summary.events.BIG_LEAD", { member, nextMember, gap: params.gap }),
          highlights: [member],
        };
      }
      case "BOTTOM_GAP": {
        const member = nameOf(params.memberId as string);
        const nextMember = nameOf(params.nextMemberId as string);
        return {
          text: connector + t("league.summary.events.BOTTOM_GAP", { member, nextMember, gap: params.gap }),
          highlights: [member],
        };
      }
      case "TIED_POSITIONS": {
        const ids = (params.memberIds as string).split(",");
        const names = ids.map(nameOf);
        if (params.lastPlaceMemberIds) {
          const lastNames = (params.lastPlaceMemberIds as string).split(",").map(nameOf);
          return {
            text: connector + t("league.summary.events.TIED_POSITIONS_withLast", {
              members: joinHuman(names, "&"), score: params.score,
              lastMembers: joinHuman(lastNames, "&"), lastScore: params.lastPlaceScore, count: lastNames.length,
            }),
            highlights: [...names, ...lastNames],
          };
        }
        return {
          text: connector + t("league.summary.events.TIED_POSITIONS", { members: joinHuman(names, "&"), score: params.score }),
          highlights: names,
        };
      }
      case "STILL_LEADING": {
        const member = nameOf(params.memberId as string);
        return {
          text: connector + t("league.summary.events.STILL_LEADING", { member, score: params.score }),
          highlights: [member],
        };
      }
      case "BLANK_GAMEWEEK": {
        if (count && count > 1) {
          const ids = (params.memberIds as string).split(",");
          const { text: members, highlights } = namesOrCount(ids.map(nameOf), "players");
          return {
            text: connector + t("league.summary.events.BLANK_GAMEWEEK", { members, count }),
            highlights,
          };
        }
        const member = nameOf(params.memberId as string);
        return {
          text: connector + t("league.summary.events.BLANK_GAMEWEEK", { member }),
          highlights: [member],
        };
      }
      case "SEASON_WINNER":
      case "SEASON_RUNNER_UP":
      case "SEASON_THIRD":
      case "SEASON_LAST": {
        if (count && count > 1) {
          const ids = (params.memberIds as string).split(",");
          const names = ids.map(nameOf);
          return {
            text: connector + t(`league.summary.events.${ev.type}`, { members: joinHuman(names, "&"), score: params.score, count }),
            highlights: names,
          };
        }
        const member = nameOf(params.memberId as string);
        return {
          text: connector + t(`league.summary.events.${ev.type}`, { member, score: params.score }),
          highlights: [member],
        };
      }
      case "SEASON_TARGET_BUSTS": {
        const ids = (params.memberIds as string).split(",");
        const names = ids.map(nameOf);
        if (count && count > 1) {
          return {
            text: connector + t("league.summary.events.SEASON_TARGET_BUSTS", { members: joinHuman(names, "&"), target: params.target, count }),
            highlights: names,
          };
        }
        return {
          text: connector + t("league.summary.events.SEASON_TARGET_BUSTS", { member: names[0], target: params.target }),
          highlights: names,
        };
      }
      case "GWC_SCORE_DIST": {
        const groups = parseGroupsStr(params.groups as string);
        const allHighlights: string[] = [];
        const parts: string[] = [];
        const nonZero = groups.filter((g) => g.score > 0);
        const zero = groups.find((g) => g.score === 0);

        if (nonZero.length === 0) {
          return { text: connector + t("league.summary.events.GWC_SCORE_DIST_all_goalless"), highlights: [] };
        }

        for (const { score, ids } of nonZero) {
          const names = ids.map(nameOf);
          const unit = score === 1 ? "pt" : "pts";
          if (ids.length > COMPACT_LIST_THRESHOLD) {
            parts.push(t("league.summary.events.GWC_SCORE_DIST_scored_count", { count: ids.length, score, unit }));
          } else if (ids.length === 1) {
            allHighlights.push(names[0]);
            parts.push(t("league.summary.events.GWC_SCORE_DIST_scored", { names: names[0], score, unit }));
          } else {
            allHighlights.push(...names);
            parts.push(t("league.summary.events.GWC_SCORE_DIST_scored_each", { names: joinHuman(names), score, unit }));
          }
        }

        if (zero && zero.ids.length > 0) {
          const names = zero.ids.map(nameOf);
          if (zero.ids.length > COMPACT_LIST_THRESHOLD) {
            parts.push(t("league.summary.events.GWC_SCORE_DIST_goalless_count", { count: zero.ids.length }));
          } else if (zero.ids.length === 1) {
            allHighlights.push(names[0]);
            parts.push(t("league.summary.events.GWC_SCORE_DIST_goalless", { names: names[0] }));
          } else {
            allHighlights.push(...names);
            parts.push(t("league.summary.events.GWC_SCORE_DIST_goalless", { names: joinHuman(names) }));
          }
        }

        return { text: connector + parts.join(", ") + ".", highlights: allHighlights };
      }
      case "SP_EXACT_DIST": {
        const groups = parseGroupsStr(params.groups as string);
        const allHighlights: string[] = [];
        const parts: string[] = [];
        const nonZero = groups.filter((g) => g.score > 0);
        const zero = groups.find((g) => g.score === 0);

        if (nonZero.length === 0) {
          return { text: connector + t("league.summary.events.SP_EXACT_DIST_all_none"), highlights: [] };
        }

        for (const { score, ids } of nonZero) {
          const names = ids.map(nameOf);
          const unit = score === 1 ? "score" : "scores";
          if (ids.length > COMPACT_LIST_THRESHOLD) {
            parts.push(t("league.summary.events.SP_EXACT_DIST_got_count", { count: ids.length, score, unit }));
          } else if (ids.length === 1) {
            allHighlights.push(names[0]);
            parts.push(t("league.summary.events.SP_EXACT_DIST_got", { names: names[0], score, unit }));
          } else {
            allHighlights.push(...names);
            parts.push(t("league.summary.events.SP_EXACT_DIST_got_each", { names: joinHuman(names), score, unit }));
          }
        }

        if (zero && zero.ids.length > 0) {
          const names = zero.ids.map(nameOf);
          if (zero.ids.length > COMPACT_LIST_THRESHOLD) {
            parts.push(t("league.summary.events.SP_EXACT_DIST_none_count", { count: zero.ids.length }));
          } else if (zero.ids.length === 1) {
            allHighlights.push(names[0]);
            parts.push(t("league.summary.events.SP_EXACT_DIST_none", { names: names[0] }));
          } else {
            allHighlights.push(...names);
            parts.push(t("league.summary.events.SP_EXACT_DIST_none", { names: joinHuman(names) }));
          }
        }

        return { text: connector + parts.join(", ") + ".", highlights: allHighlights };
      }
      default:
        return { text: "", highlights: [] };
    }
  };

  const NORMAL_CONNECTOR_COUNT = 7;

  // Builds one connector per event. A sentence whose subject(s) were all already
  // mentioned earlier reads as a natural continuation and needs no connector at all —
  // adding "As it stands, "/"Elsewhere, " there makes the flow sound strange. Connectors
  // are reserved for sentences that introduce at least one new subject.
  const buildConnectors = (events: SummaryEvent[]): string[] => {
    let normalIdx = 0;
    const mentioned = new Set<string>();
    return events.map((ev, i) => {
      let connector: string;
      const allMentioned = ev.memberIds.length > 0 && ev.memberIds.every((id) => mentioned.has(id));
      if (i === 0 || allMentioned) {
        connector = "";
      } else {
        connector = t(`league.summary.connectors.${(normalIdx++ % NORMAL_CONNECTOR_COUNT) + 1}`);
      }
      ev.memberIds.forEach((id) => mentioned.add(id));
      return connector;
    });
  };

  const title = gwSummary?.isSeasonSummary
    ? t("league.summary.seasonTitle")
    : gwSummary
      ? t("league.summary.titleWithGw", { gwName: gwSummary.gameweekShortName ?? gwSummary.gameweekName })
      : t("league.summary.title");

  return (
    <div style={panelStyle}>
      <h2 style={panelHeading}>{title}</h2>
      {!gwSummary ? (
        <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-dim)", fontStyle: "italic" }}>
          {t("league.summary.notYetAvailable")}
        </p>
      ) : gwSummary.events.length === 0 ? (
        <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-dim)", fontStyle: "italic" }}>
          {t("league.summary.noNotableEvents")}
        </p>
      ) : (
        <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--color-text)", lineHeight: 1.6 }}>
          {(() => {
            // Season summary sentences are standalone accolades, not a narrative —
            // skip connectors entirely ("Meanwhile, X finished as runner-up..." reads oddly
            // right after "X won the league...").
            const connectors = gwSummary.isSeasonSummary
              ? gwSummary.events.map(() => "")
              : buildConnectors(gwSummary.events);
            return gwSummary.events.map((ev, i) => {
              const { text, highlights } = renderSentence(ev, connectors[i]);
              return (
                <React.Fragment key={i}>
                  {i > 0 ? " " : ""}
                  {renderWithHighlights(text, highlights, String(i))}
                </React.Fragment>
              );
            });
          })()}
        </p>
      )}
    </div>
  );
}
